import { InjectionToken } from '@angular/core';
import { environment } from 'environments/environment';

/**
 * Config for the third-party analytics tools (Datadog RUM, Heap, Appcues), baked into the
 * bundle from NG_APP_* build values like every other per-environment UI setting. On the
 * Aspire/ADO pipeline they come from the `asset-accountant-ui.<env>` variable group; see
 * the AppHost's Scripts/ado-pipeline-setup.md for the keys.
 *
 * A tool is on only when its *_ENABLED flag is 'true' and its ids are set ('-' and empty
 * count as unset), so local/dev builds — which don't set the flags — have everything off.
 */
export interface AnalyticsConfig {
  datadog?: {
    env: DatadogEnvironment;
    applicationId: string;
    clientToken: string;
    sessionReplaySampleRate: number;
  };
  heap?: { appId: string };
  appcues?: { accountId: string };
}

/** Raw NG_APP_* strings, as they appear on `environment.analytics`. */
export interface AnalyticsSettings {
  datadog: {
    enabled?: string;
    env?: string;
    applicationId?: string;
    clientToken?: string;
    sessionReplaySampleRate?: string;
  };
  heap: { enabled?: string; appId?: string };
  appcues: { enabled?: string; accountId?: string };
}

// Datadog `env` tags must use the EKS environment names (not the Aspire test/stage names) so
// RUM data lines up across the AWS migration.
export const DATADOG_ENVIRONMENTS = ['inttest', 'uat', 'demo', 'prod'] as const;
export type DatadogEnvironment = (typeof DATADOG_ENVIRONMENTS)[number];

export const DATADOG_SITE = 'datadoghq.com';
export const DATADOG_SERVICE = 'fq-fixedassets-ui';
// Session replay is opt-in: an unset, '-' or invalid rate records no replays (RUM itself still runs).
const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0;

/** Returns null when every tool is off. */
export function resolveAnalyticsConfig(
  settings: AnalyticsSettings = environment.analytics,
): AnalyticsConfig | null {
  const config: AnalyticsConfig = {};
  const { datadog, heap, appcues } = settings;
  if (isEnabled(datadog.enabled)) {
    const applicationId = valueOf(datadog.applicationId);
    const clientToken = valueOf(datadog.clientToken);
    const env = valueOf(datadog.env);
    if (!isDatadogEnvironment(env)) {
      console.warn(
        `Datadog RUM disabled: NG_APP_DATADOG_ENV must be one of ${DATADOG_ENVIRONMENTS.join(', ')}.`,
      );
    } else if (applicationId && clientToken) {
      config.datadog = {
        env,
        applicationId,
        clientToken,
        sessionReplaySampleRate: toSampleRate(datadog.sessionReplaySampleRate),
      };
    }
  }

  const heapAppId = valueOf(heap.appId);
  if (isEnabled(heap.enabled) && heapAppId) {
    config.heap = { appId: heapAppId };
  }

  const appcuesAccountId = valueOf(appcues.accountId);
  if (isEnabled(appcues.enabled) && appcuesAccountId) {
    config.appcues = { accountId: appcuesAccountId };
  }

  return config.datadog || config.heap || config.appcues ? config : null;
}

export const ANALYTICS_CONFIG = new InjectionToken<AnalyticsConfig | null>('ANALYTICS_CONFIG', {
  providedIn: 'root',
  factory: () => resolveAnalyticsConfig(),
});

function isEnabled(flag: string | undefined) {
  return valueOf(flag)?.toLowerCase() === 'true';
}

// '-' is the variable groups' "intentionally unset" sentinel.
function valueOf(value: string | undefined) {
  const trimmed = value?.trim();
  return trimmed && trimmed !== '-' ? trimmed : undefined;
}

function isDatadogEnvironment(env: string | undefined): env is DatadogEnvironment {
  return DATADOG_ENVIRONMENTS.includes(env as DatadogEnvironment);
}

function toSampleRate(value: string | undefined) {
  const rate = Number(valueOf(value) ?? DEFAULT_SESSION_REPLAY_SAMPLE_RATE);
  return Number.isFinite(rate) && rate >= 0 && rate <= 100
    ? rate
    : DEFAULT_SESSION_REPLAY_SAMPLE_RATE;
}
